import type { LucideIcon } from "lucide-react";

import type { BusinessData } from "@/types/businessData";

/**
 * Context handed to every setup reminder. `connectedAppsCount` is `null` while
 * the connected-apps query is still loading — a reminder that depends on it
 * should return `"pending"` so the modal defers rather than flashing.
 */
export type SetupReminderContext = {
  businessProfile: BusinessData;
  connectedAppsCount: number | null;
};

export type SetupReminderBenefit = {
  icon: LucideIcon;
  text: string;
};

export type SetupReminderView = {
  icon: LucideIcon;
  title: string;
  description: string;
  /** "Why do this?" bullets, rendered as a benefits list in the modal. */
  benefits: SetupReminderBenefit[];
  /** Optional progress indicator (e.g. "3 av 6 steg klara"). */
  progress?: { done: number; total: number };
  cta: { label: string; to: string };
};

export type SetupReminder = {
  id: string;
  /** Higher wins when several reminders apply — only one is shown at a time. */
  priority: number;
  /**
   * Returns the view when the reminder applies, `null` when it doesn't, or
   * `"pending"` when the data needed to decide is still loading.
   */
  evaluate: (ctx: SetupReminderContext) => SetupReminderView | null | "pending";
};

/** localStorage — durable, per browser + business: user chose "don't show again". */
export function setupReminderDismissKey(
  businessId: string,
  reminderId: string,
): string {
  return `graftus_setup_reminder_dismissed:${businessId}:${reminderId}`;
}

/** sessionStorage — cleared per session: user chose "maybe later". */
export function setupReminderSnoozeKey(reminderId: string): string {
  return `graftus_setup_reminder_snoozed:${reminderId}`;
}
